import { useState, useMemo } from 'react';
import { Button, FormInstance, Tag, Form } from 'antd';
import { EditOutlined, CloseOutlined } from '@ant-design/icons';
import { BetaSchemaForm, ProFormColumnsType } from '@ant-design/pro-components';
import { useTranslation } from 'react-i18next';
import { FirmFcaData, FirmGetById } from '@pmp/api';
import { formatAddressAsLines, getHighlightProps } from '../utils';

interface AddressFieldProps {
  readonly: boolean;
  form?: FormInstance;
  getAddress?: () => any;
  fcaModifiedFields?: FirmFcaData | null | undefined;
  removeFcaModifiedField?: (key: keyof FirmFcaData, nestedKey?: string) => void;
  isFromFca?: boolean;
}

export const AddressField: React.FC<AddressFieldProps> = ({ readonly, form, getAddress, fcaModifiedFields, removeFcaModifiedField, isFromFca }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const type = Form.useWatch(['registrationDetails', 'type'], form)

  const isFcaRegistered = useMemo(() => type !== 'NOT_FCA_REGISTERED', [type]);

  const activeFcaFields = useMemo(() => (isFromFca ? fcaModifiedFields : null), [isFromFca, fcaModifiedFields]);

  const addressTitle = useMemo(
    () => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          {t('lender.firms.labels.tradingOfficeAddress')}
          {isFcaRegistered && isFromFca && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {t('lender.firms.labels.fca')}
            </Tag>
          )}
        </span>
        <div>
          {!readonly && (
            <Button type="link" icon={isEditing ? <CloseOutlined /> : <EditOutlined />} onClick={() => setIsEditing(!isEditing)} />
          )}
        </div>
      </div>
    ),
    [readonly, t, isEditing, isFcaRegistered, isFromFca],
  );

  const detailColumns: ProFormColumnsType<FirmGetById, 'text'>[] = useMemo(
    () => [
      {
        valueType: 'group',
        title: addressTitle,
        columns: [],
        colProps: { span: 24 },
      },
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('lender.firms.labels.addressLine1'),
            dataIndex: ['lineItems', 0],
            valueType: 'text' as const,
            readonly,
            fieldProps: () => ({
              placeholder: t('lender.firms.labels.addressLine1Placeholder'),
              className: getHighlightProps(['tradingNames'], activeFcaFields),
              onBlur: () => removeFcaModifiedField?.('tradingNames'),
            }),
            formItemProps: { rules: [{ required: true }] },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.addressLine2'),
            dataIndex: ['lineItems', 1],
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              placeholder: t('lender.firms.labels.addressLine2Placeholder'),
              className: getHighlightProps(['tradingNames'], activeFcaFields),
              onBlur: () => removeFcaModifiedField?.('tradingNames'),
            },
            formItemProps: { rules: [{ required: true }] },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('lender.firms.labels.addressLine3'),
            dataIndex: ['lineItems', 2],
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              className: getHighlightProps(['tradingNames'], activeFcaFields),
              onBlur: () => removeFcaModifiedField?.('tradingNames'),
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.addressLine4'),
            dataIndex: ['lineItems', 3],
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              className: getHighlightProps(['tradingNames'], activeFcaFields),
              onBlur: () => removeFcaModifiedField?.('tradingNames'),
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('lender.firms.labels.town'),
            dataIndex: ['city'],
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              placeholder: t('lender.firms.labels.addressLine2Placeholder'),
              className: getHighlightProps(['tradingNames'], activeFcaFields),
              onBlur: () => removeFcaModifiedField?.('tradingNames'),
            },
            formItemProps: { rules: [{ required: true }] },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.county'),
            dataIndex: ['lineItems', 4],
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              placeholder: t('lender.firms.labels.addressLine2Placeholder'),
              className: getHighlightProps(['tradingNames'], activeFcaFields),
              onBlur: () => removeFcaModifiedField?.('tradingNames'),
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('lender.firms.labels.postCode'),
            dataIndex: ['postCode'],
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              placeholder: t('lender.firms.labels.addressLine2Placeholder'),
              className: getHighlightProps(['tradingNames'], activeFcaFields),
              onBlur: () => removeFcaModifiedField?.('tradingNames'),
            },
            formItemProps: { rules: [{ required: true }] },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.country'),
            dataIndex: ['country'],
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              placeholder: t('lender.firms.labels.addressLine2Placeholder'),
              className: getHighlightProps(['tradingNames'], activeFcaFields),
              onBlur: () => removeFcaModifiedField?.('tradingNames'),
            },
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [t, readonly, addressTitle, activeFcaFields, removeFcaModifiedField],
  );

  const previewColumns: ProFormColumnsType<FirmGetById, 'text'>[] = useMemo(
    () => [
      {
        title: addressTitle,
        valueType: 'group' as const,
        columns: [],
        colProps: { span: 12 },
      },
      {
        dataIndex: 'lineItems',
        valueType: 'textarea' as const,
        fieldProps: () => {
          const addr = getAddress?.();
          return {
            value: formatAddressAsLines(addr),
            autoSize: { minRows: 2 },
            disabled: true,
            className: getHighlightProps(['tradingNames'], activeFcaFields),
          };
        },
        colProps: { span: 12 },
      },
    ],
    [getAddress, addressTitle, activeFcaFields],
  );

  return (
    <BetaSchemaForm<FirmGetById>
      form={form}
      layoutType="Embed"
      columns={isEditing ? detailColumns : previewColumns}
      submitter={false}
    />
  );
};
